/*
 * MCRO001-F0050 / T-MCRO-050 (design: choreo MCRO001-D004 §4.1.3, commit 84538abd)
 * Slice 1 of 3 — the structured annotation / conflict AST node model.
 *
 * GENERIC: no choreography imports. `kind` is an OPEN string, not a closed union —
 * an unknown kind is a VALID node (it later renders as a plain labelled box; that
 * rendering is slice B). Here the node is purely a data shape + light constructors.
 */

/**
 * @typedef {Object} AnnotationNode
 * @property {string} kind                  Machine type. OPEN string (§4.1). Unknown kinds valid.
 * @property {string} [title]               Optional human border label (typed boxes only).
 * @property {string} id                    Stable identity for picker binding / telemetry.
 * @property {string} [base]                Ancestor content (conflict/compare).
 * @property {string} [ours]                Our side.
 * @property {string} [theirs]              Their side.
 * @property {string} [body]                Free text (comment/suggest, or plain box body).
 * @property {Object.<string,string>} [attributes]  Forward-compatible labelled fields (§4.4).
 * @property {{choice:('ours'|'theirs'|'both'|'edit'), text?:string, at?:string}} [resolution]
 * @property {AnnotationNode[]} [children]  Nested annotation nodes.
 */

/**
 * The well-known kinds this slice is aware of. `kind` is a plain string, NOT this set —
 * this const exists for callers that want to branch on the known kinds; it does NOT
 * close the type. A kind outside this list is still a valid node.
 * @type {readonly string[]}
 */
export const WELL_KNOWN_KINDS = Object.freeze(['conflict', 'comment', 'suggest', 'compare']);

/**
 * True iff `kind` is one of the well-known kinds. Unknown kinds are still valid nodes;
 * this is a convenience predicate, not a validation gate.
 * @param {string} kind
 * @returns {boolean}
 */
export function isWellKnownKind(kind) {
  return WELL_KNOWN_KINDS.includes(kind);
}

/**
 * True iff `value` is a plausible AnnotationNode shape. Deliberately permissive on
 * `kind` (any non-empty string) to honour the open-kind rule. Does NOT throw.
 * @param {any} value
 * @returns {boolean}
 */
export function isAnnotationNode(value) {
  return Boolean(
    value &&
      typeof value === 'object' &&
      typeof value.kind === 'string' &&
      value.kind.length > 0 &&
      typeof value.id === 'string',
  );
}

/**
 * Construct a well-formed AnnotationNode with only defined optional fields present, so
 * two nodes constructed from equivalent inputs are deep-equal (important for the
 * round-trip `parse(serialize(n)) === n` tests). `id` is REQUIRED and supplied by the
 * caller — this module never generates ids, keeping it pure (no rng/clock).
 *
 * `id` is OPTIONAL and defaults to '' — a parsed box that carried no `id:` field is a
 * valid node with an empty id (ids are minted by the producer, slice B, not the lexer).
 * When present it must be a string.
 *
 * `title` of '' is treated as ABSENT (a typed box with an empty title slot has no title),
 * which keeps serialize/parse round-trips symmetric.
 *
 * @param {Partial<AnnotationNode> & { kind: string }} fields
 * @returns {AnnotationNode}
 */
export function createAnnotationNode(fields) {
  if (!fields || typeof fields.kind !== 'string' || fields.kind.length === 0) {
    throw new Error('createAnnotationNode: `kind` must be a non-empty string.');
  }
  if (fields.id !== undefined && typeof fields.id !== 'string') {
    throw new Error('createAnnotationNode: `id` must be a string when present.');
  }
  const node = { kind: fields.kind, id: fields.id === undefined ? '' : fields.id };
  if (fields.title !== undefined && fields.title !== '') node.title = fields.title;
  if (fields.base !== undefined) node.base = fields.base;
  if (fields.ours !== undefined) node.ours = fields.ours;
  if (fields.theirs !== undefined) node.theirs = fields.theirs;
  if (fields.body !== undefined) node.body = fields.body;
  if (fields.attributes !== undefined && Object.keys(fields.attributes).length > 0) {
    node.attributes = { ...fields.attributes };
  }
  if (fields.resolution !== undefined) node.resolution = fields.resolution;
  if (fields.children !== undefined && fields.children.length > 0) {
    node.children = fields.children;
  }
  return node;
}
